<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { localReq, postReq } from '../../../../services/http';
	import { format } from '../../../../services/i18n';
	//Stores
		//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	//Design system components
	import { Button, Toggle, Checkbox, Input, toast, Alert, Text } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { capFirstChar } from 'src/helpers/utilities';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	const id = real_id(device.id);
	const min = 10;
	const max = 100;
	let settings = $settingsStore;

	let brightness = settings?.warm_up?.brightness,
		enabled = settings?.warm_up?.enabled,
		warmTime = settings?.warm_up?.time,
		localIp = get_local_ip(id),
		loading = false;
	let cacheBrightness = brightness,
		cacheEnabled = enabled,
		cacheWarmTime = warmTime;

	const setEnabledLocal = async () => {
		const res = await localReq(device, `${localIp}/settings/warm_up`, 'post', {
			enabled: enabled ? 1 : 0,
		});
		return res.isok;
	};

	const setBrightnessLocal = async () => {
		const res = await localReq(device, `${localIp}/settings/warm_up`, 'post', {
			brightness: brightness,
		});
		return res.isok;
	};

	const setWarmTimeLocal = async () => {
		const res = await localReq(device, `${localIp}/settings/warm_up`, 'post', {
			time: warmTime,
		});
		return res.isok;
	};

	const saveSettings = () => {
		settings.warm_up.enabled = enabled;
		settings.warm_up.brightness = brightness;
		settings.warm_up.time = warmTime;
		cacheBrightness = brightness;
		cacheEnabled = enabled;
		cacheWarmTime = warmTime;
		toast.open({
			type: 'success',
			title: enabled ? $format('warm_up_save_scc_toast') : $format('warm_up_disabled_toast'),
		});
		devices_settings.update_devices_settings(id, settings);
	};

	const handleToggle = () => {
		enabled = !enabled;
		if ($showNewLayout) {
			sentRequests();
		}
	};
	const revertValues = () => {
		brightness = cacheBrightness;
		enabled = cacheEnabled;
		warmTime = cacheWarmTime;
	};
	const sentRequests = async () => {
		if (cacheBrightness !== brightness || cacheEnabled !== enabled || cacheWarmTime !== warmTime) {
			loading = true;
			if (localIp) {
				Promise.all([setBrightnessLocal(), setWarmTimeLocal(), setEnabledLocal()]).then(
					(res) => {
						if (res.some((isok) => !isok)) {
							toast.open({ type: 'danger', title: $format('error_global') });
							revertValues();
						} else {
							saveSettings();
						}
					},
				);
			} else {
				const res = await postReq(`/device/${device.category}/settings/warm_up`, {
					time: warmTime,
					brightness: brightness,
					enabled: enabled ? 1 : 0,
					channel: device.channel,
					id: id,
				});
				if (res.isok) {
					saveSettings();
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					revertValues();
				}
			}
			loading = false;
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enabled) {
			stickyBottomAction.set({ onClick: sentRequests, loading, disabled: false });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle size="m" class="mb-s-plus" checked={enabled} on:click={handleToggle} id="checkbox-43704bs">
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Toggle>
	<Alert class="mb-s-plus" heading={$format('warm_up_description')} />
	{#if enabled}
		<Text styleType="body-small" class="mb-s">
			{$format('warm_up_brightness_label', {
				default: 'Warm-up brightness',
			})}
		</Text>
		<Input
			size="m"
			class="mb-s-plus"
			type={'number'}
			{min}
			{max}
			info={$format('warm_up_brightness_set_value', {
				default: 'The value must be between 10-100%',
			})}
			bind:value={brightness}
			on:enter={sentRequests}
			id="input-0ebkosr"
			aria-label={$format('warm_up_brightness_set', {
				default: 'Warmup brightness',
			})}
		/>

		<Text styleType="body-small" class="mb-s">
			{$format('warm_up_time_label', {
				default: 'Warm-up time',
			})}
		</Text>
		<Input
			size="m"
			bind:value={warmTime}
			type={'number'}
			icon="fa-solid fa-clock"
			min={20}
			max={1000}
			info={$format('warm_up_time_set_value', {
				default: 'The value must be between 20-1000 milliseconds',
			})}
			on:enter={sentRequests}
			id="input-oqp5aox"
			aria-label={$format('warm_up_time_set', {
				default: 'Warmup time',
			})}
		/>
	{/if}
{:else}
	<Checkbox size="m" class="mb-s" checked={enabled} on:click={handleToggle} id="checkbox-43704bs"
		>{$format('warm_up_description')}</Checkbox
	>
	{#if enabled}
		<Input
			size="m"
			class="mb-s"
			type={'number'}
			{min}
			{max}
			info={$format('warm_up_brightness_set')}
			bind:value={brightness}
			on:enter={sentRequests}
			id="input-0ebkosr"
			aria-label={$format('warm_up_brightness_set', {
				default: 'Warmup brightness',
			})}
		/>

		<Input
			size="m"
			class="mb-s"
			bind:value={warmTime}
			type={'number'}
			min={20}
			max={1000}
			info={$format('warm_up_time_set')}
			on:enter={sentRequests}
			id="input-oqp5aox"
			aria-label={$format('warm_up_time_set', {
				default: 'Warmup time',
			})}
		/>
	{/if}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sentRequests}
		>{$format('button_save')}</Button
	>
{/if}
